import { Component, AfterViewInit, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PartnerGroup } from '../interfaces/partners.interface';
import { NumberFormatStyle } from '@angular/common';
import { ScrollPositionService } from '../services/scroll-position.service';
import { PartnersService } from '../services/partners.service';

@Component({
  selector: 'app-offer',
  standalone: false,
  templateUrl: './offer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer.component.scss']
})
export class OfferComponent implements OnInit, AfterViewInit {
  selectedGroup?: number | null;
  groups: PartnerGroup[] = [];
  categoryFromUrl?: string;
  categoryTitle?: string;
  groupsLoaded = false; // Track if groups are loaded to prevent premature rendering

  constructor(
    private scrollPositionService: ScrollPositionService,
    private route: ActivatedRoute,
    private router: Router,
    private partnersService: PartnersService,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    // Subscribe to route parameters first
    this.route.params.subscribe(params => {
      this.categoryFromUrl = params['categoryName'];
      // Don't process the category yet - wait for groups to load
    });
    
    // Load partner groups and process category after groups are loaded
    this.loadGroups();
  }

  loadGroups(): void {
    this.partnersService.getPartnerGroup().subscribe({
      next: (groups) => {
        this.groups = groups;
        
        // Now that groups are loaded, process the URL category
        if (this.categoryFromUrl) {
          this.findAndSelectGroupByName(this.categoryFromUrl);
        } else {
          // No category in URL, clear selection
          this.selectedGroup = null;
          this.categoryTitle = undefined;
          this.updatePageTitle();
        }
        
        // Mark groups as loaded so the template can render the offer-list
        this.groupsLoaded = true;
      },
      error: () => {
        this.groups = [];
        this.selectedGroup = null;
        this.groupsLoaded = true; // Allow rendering even on error
      }
    });
  }

  findAndSelectGroupByName(categoryName: string): void {
    // Convert URL-friendly name back to match group titles
    const searchName = categoryName.replace(/-/g, ' ').toLowerCase();
    
    // Helper function to normalize text for comparison (handles Polish characters)
    const normalizeText = (text: string): string => {
      return text.toLowerCase()
        .replace(/ą/g, 'a')
        .replace(/ć/g, 'c')
        .replace(/ę/g, 'e')
        .replace(/ł/g, 'l')
        .replace(/ń/g, 'n')
        .replace(/ó/g, 'o')
        .replace(/ś/g, 's')
        .replace(/ź/g, 'z')
        .replace(/ż/g, 'z')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    const normalizedSearchName = normalizeText(searchName);
    
    // Find group by matching title (case-insensitive with Polish character normalization)
    const matchingGroup = this.groups.find(group => {
      const normalizedGroupTitle = normalizeText(group.title);
      return normalizedGroupTitle === normalizedSearchName ||
             group.title.toLowerCase() === searchName;
    });
    
    if (matchingGroup) {
      this.selectedGroup = matchingGroup.id;
      this.categoryTitle = matchingGroup.title;
      this.updatePageTitle(matchingGroup.title);
    } else {
      // If no exact match found, try partial matching
      const partialMatch = this.groups.find(group => {
        const normalizedGroupTitle = normalizeText(group.title);
        return normalizedGroupTitle.includes(normalizedSearchName) ||
               normalizedSearchName.includes(normalizedGroupTitle) ||
               group.title.toLowerCase().includes(searchName) ||
               searchName.includes(group.title.toLowerCase());
      });
      
      if (partialMatch) {
        this.selectedGroup = partialMatch.id;
        this.categoryTitle = partialMatch.title;
        this.updatePageTitle(partialMatch.title);
      } else {
        this.selectedGroup = null;
        this.categoryTitle = undefined;
        this.updatePageTitle();
      }
    }
  }

  onGroupSelected(groupId: number | null): void {
    // Prevent duplicate loading if the selection hasn't actually changed
    if (this.selectedGroup === groupId) {
      return;
    }
    
    this.selectedGroup = groupId;
    
    // Update category title based on selection
    if (groupId) {
      const selectedGroup = this.groups.find(group => group.id === groupId);
      this.categoryTitle = selectedGroup?.title;
      this.updatePageTitle(selectedGroup?.title);
    } else {
      this.categoryTitle = undefined;
      this.updatePageTitle();
    }
  }

  updatePageTitle(categoryName?: string): void {
    if (categoryName) {
      this.titleService.setTitle(`Oferty - ${categoryName} | Zgrane Grono`);
    } else {
      this.titleService.setTitle('Oferty naszych partnerów | Zgrane Grono');
    }
  }

  ngAfterViewInit(): void {
    // Don't restore scroll position here anymore - wait for loading to complete
  }

  onLoadingComplete(): void {
    // Restore scroll position after data is loaded
    setTimeout(() => {
      const currentRoute = this.router.url; // Use actual current route instead of hardcoded '/oferty'
      this.scrollPositionService.restorePosition(currentRoute);
    }, 100);
  }
}
